import React, {useEffect, useState} from "react";
import {BeatLoader} from "react-spinners";
import {toast} from "react-toastify";
import {useStateValue} from "../../../states/StateProvider";
import {AES, enc} from "crypto-js";
import {useNavigate} from "react-router";
import {placeholderAPI, initializeiFExAuth} from "../../../Api/ifex";
import PlaceholderCreateModal from "./PlaceholderCreateModal";
import PlaceholderEditModal from "./PlaceholderEditModal";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";
import Pagination from "../../../components/pagination";
import {FiEdit2, FiTrash2, FiPlus, FiSearch, FiFilter} from "react-icons/fi";
import {RiArrowDownSFill, RiArrowUpSFill} from "react-icons/ri";

const PlaceholderManagement = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(false);
    const [{secretKey}, dispatch] = useStateValue();
    const navigate = useNavigate();

    // Data state
    const [placeholders, setPlaceholders] = useState([]);
    const [pagination, setPagination] = useState({});

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({
        id: '',
        search: '',
        placeholder_name: '',
        is_active: '', // '' = all, '1' = active, '0' = inactive
        prefix: '', // M_, F_, A_, P_, or '' for all
        placeholder_type: '', // standard, required, formfield, textchain
        table_name: '',
        field_name: '',
        field_format: '',
        created_by: '',
    });

    // Applied filters state (only used when "Filter starten" is clicked)
    const [appliedFilters, setAppliedFilters] = useState({});

    // Check if filters have changed
    const hasFilterChanges = () => {
        return Object.keys(filters).some(key => {
            const currentValue = filters[key];
            const appliedValue = appliedFilters[key];
            return currentValue !== appliedValue && currentValue !== '';
        });
    };

    // Sorting state
    const [sorting, setSorting] = useState({
        field: 'id',
        direction: 'asc'
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);

    // Check if user is Super-Admin
    const isSuperAdmin = user?.isSAdmin === '1' || user?.role === 'Super Admin';

    useEffect(() => {
        try {
            const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
            const userData = JSON.parse(decryptedBytes.toString(enc.Utf8));

            if (!userData) {
                navigate('/anmeldung');
                return;
            }

            setUser(userData);
            setLoading(false);
        } catch (e) {
            console.error('Authentication error:', e);
            window.location.replace('/anmeldung');
        }
    }, [secretKey, navigate]);

    useEffect(() => {
        if (user) {
            fetchPlaceholders();
        }
    }, [user, appliedFilters, sorting, currentPage, perPage]);

    const fetchPlaceholders = async () => {
        setDataLoading(true);
        try {
            // Initialize iFEx authentication first
            const authSuccess = await initializeiFExAuth();
            if (!authSuccess) {
                toast.error('iFEx Authentifizierung fehlgeschlagen');
                setDataLoading(false);
                return;
            }

            const params = {
                page: currentPage,
                per_page: perPage,
                sort_field: sorting.field,
                sort_direction: sorting.direction,
                ...(appliedFilters.search && { search: appliedFilters.search }),
                ...(appliedFilters.id && { id: appliedFilters.id }),
                ...(appliedFilters.placeholder_name && { placeholder_name: appliedFilters.placeholder_name }),
                ...(appliedFilters.is_active !== '' && { is_active: appliedFilters.is_active }),
                ...(appliedFilters.prefix && { prefix: appliedFilters.prefix }),
                ...(appliedFilters.placeholder_type && { placeholder_type: appliedFilters.placeholder_type }),
                ...(appliedFilters.table_name && { table_name: appliedFilters.table_name }),
                ...(appliedFilters.field_name && { field_name: appliedFilters.field_name }),
                ...(appliedFilters.field_format && { field_format: appliedFilters.field_format }),
                ...(appliedFilters.created_by && { created_by: appliedFilters.created_by }),
            };

            const response = await placeholderAPI.getAll(params);

            // Handle response data
            if (response.data) {
                const data = response.data.data || response.data;
                const meta = response.data.meta || {};

                // Normalize pagination meta (backend returns arrays instead of single values)
                const normalizedMeta = {
                    current_page: Array.isArray(meta.current_page) ? meta.current_page[0] : meta.current_page,
                    from: Array.isArray(meta.from) ? meta.from[0] : meta.from,
                    to: Array.isArray(meta.to) ? meta.to[0] : meta.to,
                    per_page: Array.isArray(meta.per_page) ? meta.per_page[0] : meta.per_page,
                    total: Array.isArray(meta.total) ? meta.total[0] : meta.total,
                    last_page: Array.isArray(meta.last_page) ? meta.last_page[0] : meta.last_page,
                    links: meta.links || []
                };

                setPlaceholders(Array.isArray(data) ? data : []);
                setPagination(normalizedMeta);
            }
        } catch (error) {
            console.error('Error fetching placeholders:', error);
            toast.error('Fehler beim Laden der Platzhalter');
        } finally {
            setDataLoading(false);
        }
    };

    const handleSort = (field, direction) => {
        if (direction) {
            // Direct direction specified from arrow click
            setSorting({ field, direction });
        } else {
            // Toggle direction
            setSorting(prev => ({
                field,
                direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
        }
    };

    const handleEdit = (placeholder) => {
        setSelectedPlaceholder(placeholder);
        setShowEditModal(true);
    };

    const handleDelete = (placeholder) => {
        setSelectedPlaceholder(placeholder);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        setDeleteLoading(true);
        try {
            await placeholderAPI.delete(selectedPlaceholder.id);
            toast.success('Platzhalter erfolgreich gelöscht');
            setShowDeleteModal(false);
            setSelectedPlaceholder(null);
            fetchPlaceholders();
        } catch (error) {
            console.error('Error deleting placeholder:', error);
            if (error.response?.status === 409) {
                toast.error('Platzhalter kann nicht gelöscht werden, da er in Dokumenten verwendet wird');
            } else {
                toast.error('Fehler beim Löschen des Platzhalters');
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        setAppliedFilters({...filters});
        setCurrentPage(1); // Reset to first page when applying filters
    };

    const clearFilters = () => {
        const emptyFilters = {
            id: '',
            search: '',
            placeholder_name: '',
            is_active: '',
            prefix: '',
            placeholder_type: '',
            table_name: '',
            field_name: '',
            field_format: '',
            created_by: '',
        };
        setFilters(emptyFilters);
        setAppliedFilters({});
        setCurrentPage(1);
    };

    const SortIcon = ({ field }) => {
        if (sorting.field !== field) return <span className="text-gray-300 ml-1">↕</span>;
        return sorting.direction === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    };

    if (loading) {
        return (
            <div className='dashboardContainer flex justify-center items-center'>
                <BeatLoader color="#1e3a8a" size={15} />
            </div>
        );
    }

    return (
        <div className='dashboardContainer'>
            {/* Header */}
            <div className='flex justify-between items-center content-center pb-5'>
                <h2 className='text-2xl font-bold text-text text-left'>Platzhalter-Verwaltung</h2>
                <div className='flex gap-3 items-center'>
                    {/* Filter Action Buttons - Show only if filters changed */}
                    {hasFilterChanges() && (
                        <>
                            <button
                                onClick={applyFilters}
                                className='inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                            >
                                <FiFilter className="mr-2" /> Filter starten
                            </button>
                            <button
                                onClick={clearFilters}
                                className='inline-flex items-center px-4 py-2 border border-mainBlue shadow-sm text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                            >
                                Filter zurücksetzen
                            </button>
                        </>
                    )}
                    {isSuperAdmin && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className='inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                        >
                            <FiPlus className="mr-2" /> Neuer Platzhalter
                        </button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className='bg-white rounded-lg shadow-lg border border-borderBlue px-6 py-4'>
                {dataLoading ? (
                    <div className='flex justify-center items-center h-72'>
                        <BeatLoader size={12} color="#3A46A9" />
                    </div>
                ) : placeholders.length > 0 ? (
                    <>
                        <div className='overflow-x-auto'>
                            <table className='w-full' style={{fontSize: "14px"}}>
                                <thead className="bg-white border-y border-silver sticky top-0">
                                    <tr>
                                        {/* ID */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span className={`${sorting.field === 'id' && 'text-mainBlue'}`}>ID</span>
                                                <div className='flex flex-col'>
                                                    <RiArrowUpSFill 
                                                        size='22px' 
                                                        className={`cursor-pointer ${sorting.field === 'id' && sorting.direction === 'asc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('id', 'asc')}
                                                    />
                                                    <RiArrowDownSFill 
                                                        size='22px' 
                                                        className={`-mt-3.5 cursor-pointer ${sorting.field === 'id' && sorting.direction === 'desc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('id', 'desc')}
                                                    />
                                                </div>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-16 h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.id || ''}
                                                onChange={(e) => handleFilterChange('id', e.target.value)}
                                            />
                                        </th>

                                        {/* Platzhalter Name */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span className={`${sorting.field === 'placeholder_name' && 'text-mainBlue'}`}>Platzhalter</span>
                                                <div className='flex flex-col'>
                                                    <RiArrowUpSFill 
                                                        size='22px' 
                                                        className={`cursor-pointer ${sorting.field === 'placeholder_name' && sorting.direction === 'asc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('placeholder_name', 'asc')}
                                                    />
                                                    <RiArrowDownSFill 
                                                        size='22px' 
                                                        className={`-mt-3.5 cursor-pointer ${sorting.field === 'placeholder_name' && sorting.direction === 'desc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('placeholder_name', 'desc')}
                                                    />
                                                </div>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-full h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.placeholder_name || ''}
                                                onChange={(e) => handleFilterChange('placeholder_name', e.target.value)}
                                            />
                                        </th>

                                        {/* Status */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span className={`${sorting.field === 'is_active' && 'text-mainBlue'}`}>Status</span>
                                                <div className='flex flex-col'>
                                                    <RiArrowUpSFill 
                                                        size='22px' 
                                                        className={`cursor-pointer ${sorting.field === 'is_active' && sorting.direction === 'asc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('is_active', 'asc')}
                                                    />
                                                    <RiArrowDownSFill 
                                                        size='22px' 
                                                        className={`-mt-3.5 cursor-pointer ${sorting.field === 'is_active' && sorting.direction === 'desc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('is_active', 'desc')}
                                                    />
                                                </div>
                                            </div>
                                            <select
                                                className='w-full h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.is_active || ''}
                                                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                                            >
                                                <option value=''>Alle</option>
                                                <option value='1'>Aktiv</option>
                                                <option value='0'>Inaktiv</option>
                                            </select>
                                        </th>

                                        {/* Typ */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Typ</span>
                                            </div>
                                            <select
                                                className='w-full h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.placeholder_type || ''}
                                                onChange={(e) => handleFilterChange('placeholder_type', e.target.value)}
                                            >
                                                <option value=''>Alle</option>
                                                <option value='standard'>Standard</option>
                                                <option value='required'>Pflichtfeld</option>
                                                <option value='formfield'>Formularfeld</option>
                                                <option value='textchain'>Textkette</option>
                                            </select>
                                        </th>

                                        {/* Tabelle */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Tabelle</span>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-full h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.table_name || ''}
                                                onChange={(e) => handleFilterChange('table_name', e.target.value)}
                                            />
                                        </th>

                                        {/* Feld */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Feld</span>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-full h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.field_name || ''}
                                                onChange={(e) => handleFilterChange('field_name', e.target.value)}
                                            />
                                        </th>

                                        {/* Format */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Format</span>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-32 h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.field_format || ''}
                                                onChange={(e) => handleFilterChange('field_format', e.target.value)}
                                            />
                                        </th>

                                        {/* Erstellt */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span className={`${sorting.field === 'created_at' && 'text-mainBlue'}`}>Erstellt</span>
                                                <div className='flex flex-col'>
                                                    <RiArrowUpSFill 
                                                        size='22px' 
                                                        className={`cursor-pointer ${sorting.field === 'created_at' && sorting.direction === 'asc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('created_at', 'asc')}
                                                    />
                                                    <RiArrowDownSFill 
                                                        size='22px' 
                                                        className={`-mt-3.5 cursor-pointer ${sorting.field === 'created_at' && sorting.direction === 'desc' ? 'text-mainBlue' : 'text-grey'}`}
                                                        onClick={() => handleSort('created_at', 'desc')}
                                                    />
                                                </div>
                                            </div>
                                            <div className='w-24 h-8 mt-2'></div>
                                        </th>

                                        {/* Benutzer */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Benutzer</span>
                                            </div>
                                            <input
                                                type='text'
                                                placeholder='Suche...'
                                                className='w-24 h-8 px-2 py-1 mt-2 border border-borderBlue rounded text-xs'
                                                value={filters.created_by || ''}
                                                onChange={(e) => handleFilterChange('created_by', e.target.value)}
                                            />
                                        </th>

                                        {/* Letzter Zugriff */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Letzter Zugriff</span>
                                            </div>
                                            <div className='w-24 h-8 mt-2'></div>
                                        </th>

                                        {/* Aktionen */}
                                        <th className='text-left py-2 text-grey font-semibold pr-3'>
                                            <div className='flex items-center justify-start'>
                                                <span>Aktionen</span>
                                            </div>
                                            <div className='w-20 h-8 mt-2'></div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {placeholders.map((placeholder) => (
                                        <tr
                                            key={placeholder.id}
                                            className='border-y border-silver text-sm hover:bg-offWhite cursor-pointer transition'
                                            onClick={() => handleEdit(placeholder)}
                                        >
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.id}</td>
                                            <td className='py-2 text-mainBlue underline font-light pr-3 font-mono'>{placeholder.placeholder_name}</td>
                                            <td className='py-2 font-light pr-3'>
                                                <span className={`px-2 py-1 rounded text-xs ${placeholder.is_active ? 'bg-complete text-green' : 'bg-redLight text-red'}`}>
                                                    {placeholder.is_active ? 'Aktiv' : 'Inaktiv'}
                                                </span>
                                            </td>
                                            <td className='py-2 text-text font-light pr-3 capitalize'>{placeholder.placeholder_type}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.table_name || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.field_name || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.field_format || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.created_at ? new Date(placeholder.created_at).toLocaleDateString('de-DE') : '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.created_by || '-'}</td>
                                            <td className='py-2 text-text font-light pr-3'>{placeholder.last_accessed_at ? new Date(placeholder.last_accessed_at).toLocaleDateString('de-DE') : '-'}</td>
                                            <td className='py-2 font-light pr-3' onClick={(e) => e.stopPropagation()}>
                                                <div className='flex gap-3'>
                                                    <button
                                                        onClick={() => handleEdit(placeholder)}
                                                        className='text-mainBlue hover:text-lightBlue transition'
                                                        title='Bearbeiten'
                                                    >
                                                        <FiEdit2 size={16} />
                                                    </button>
                                                    {isSuperAdmin && (
                                                        <button
                                                            onClick={() => handleDelete(placeholder)}
                                                            className='text-cancel hover:text-red transition'
                                                            title='Löschen'
                                                        >
                                                            <FiTrash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination && pagination.total > 0 && (
                            <div className='flex justify-between items-center mt-6 pt-4 border-t border-borderBlue'>
                                <div className='text-sm text-grey'>
                                    {pagination.from} bis {pagination.to} von {pagination.total} Einträge
                                </div>
                                <div className='flex items-center gap-2'>
                                    <span className='text-sm text-grey'>Einträge anzeigen:</span>
                                    <select
                                        className='border border-borderBlue rounded px-2 py-1 text-sm text-text focus:border-mainBlue focus:outline-none'
                                        value={perPage}
                                        onChange={(e) => {
                                            setPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="15">15</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                                {pagination.last_page > 1 && (
                                    <Pagination
                                        currentPage={currentPage}
                                        totalCount={pagination.total}
                                        pageSize={perPage}
                                        onPageChange={page => setCurrentPage(page)}
                                    />
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className='text-center py-16 bg-offWhite rounded'>
                        <div className='inline-flex items-center justify-center w-16 h-16 bg-mainBlue bg-opacity-10 rounded-full mb-4'>
                            <FiSearch className='h-8 w-8 text-mainBlue' />
                        </div>
                        <h3 className='text-lg font-semibold text-text mb-2'>Keine Platzhalter gefunden</h3>
                        <p className='text-base text-grey mb-6'>
                            {filters.search || filters.is_active || filters.prefix || filters.placeholder_type
                                ? 'Versuchen Sie, die Filter anzupassen oder zurückzusetzen.'
                                : 'Erstellen Sie Ihren ersten Platzhalter, um zu beginnen.'}
                        </p>
                        {isSuperAdmin && !filters.search && !filters.is_active && !filters.prefix && !filters.placeholder_type && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className='inline-flex items-center px-6 py-3 border border-transparent shadow-md text-sm font-semibold rounded-lg text-white bg-mainBlue hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-all'
                            >
                                Ersten Platzhalter erstellen
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showCreateModal && (
                <PlaceholderCreateModal
                    visible={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        fetchPlaceholders();
                    }}
                    user={user}
                />
            )}

            {showEditModal && selectedPlaceholder && (
                <PlaceholderEditModal
                    visible={showEditModal}
                    placeholder={selectedPlaceholder}
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedPlaceholder(null);
                    }}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedPlaceholder(null);
                        fetchPlaceholders();
                    }}
                    user={user}
                    isSuperAdmin={isSuperAdmin}
                />
            )}

            {showDeleteModal && selectedPlaceholder && (
                <ConfirmationModal
                    visible={showDeleteModal}
                    title='Platzhalter löschen?'
                    message={`Möchten Sie den Platzhalter "${selectedPlaceholder.placeholder_name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
                    confirmText='Löschen'
                    cancelText='Abbrechen'
                    onConfirm={confirmDelete}
                    onCancel={() => {
                        setShowDeleteModal(false);
                        setSelectedPlaceholder(null);
                    }}
                    loading={deleteLoading}
                />
            )}
        </div>
    );
};

export default PlaceholderManagement;
